import {
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import Box from "@mui/material/Box";
import { useEffect, useState, MouseEvent, useRef } from "react";
import { useDeleteHealthLeaf } from "../../hooks/useDeleteHealthLeaf";
import { useSetExpectedOnline } from "../../hooks/useSetExpectedOnline";
import { useUpdateHealthLeafMetadata } from "../../hooks/useUpdateHealthLeafMetadata";

interface ToggleTrackingDialogProps {
  open: boolean;
  targetLabel: string;
  entityType: "location" | "node" | "device" | "model";
  onClose: () => void;
  entityId: string;
  locationId: string;
  initialIsExpectedOnline?: boolean;
  existingReason?: string;
  existingAssignedWatcher?: string;
  leafPk?: string;
  leafSk?: string;
  allowErrorReason?: boolean;
}

const REASONS_BY_ENTITY_TYPE: Record<
  ToggleTrackingDialogProps["entityType"],
  string[]
> = {
  location: [
    "Onboarding",
    "Zone setup",
    "Site Power Issue",
    "Customer Network",
  ],
  node: ["SSD failure", "General Hardware"],
  device: ["Device Failure"],
  model: ["Customer Requested Model turnoff"],
};

const ENTITY_TYPE_LABEL: Record<
  ToggleTrackingDialogProps["entityType"],
  string
> = {
  location: "Location",
  node: "Node",
  device: "Device",
  model: "Service",
};

const ToggleTrackingDialog = ({
  open,
  targetLabel,
  entityType,
  onClose,
  entityId,
  locationId,
  leafPk,
  leafSk,
  initialIsExpectedOnline,
  existingReason,
  existingAssignedWatcher,
  allowErrorReason = false,
}: ToggleTrackingDialogProps): JSX.Element => {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [assignedWatcher, setAssignedWatcher] = useState<
    "CUSTOMER" | "THIRD_PARTY" | "CLEANCONNECT"
  >("CLEANCONNECT");
  const [customReason, setCustomReason] = useState<string>("");
  const [errorReason, setErrorReason] = useState<string>("");
  const [customErrorReason, setCustomErrorReason] = useState<string>("");
  const { setExpectedOnline } = useSetExpectedOnline();
  const { deleteHealthLeaf, loading: deleting } = useDeleteHealthLeaf();
  const { updateHealthLeafMetadata, loading: updating } =
    useUpdateHealthLeafMetadata();
  const hideReasonForLocationFlipToOn =
    entityType === "location" && initialIsExpectedOnline === false;

  // For N/D/S entities, only show reason if entity is NOT green (online)
  // Show reason for: red (offline but expected online) or grey (expected offline)
  const shouldShowReason =
    entityType === "location"
      ? !hideReasonForLocationFlipToOn
      : allowErrorReason || initialIsExpectedOnline === false;
  // Determine if clicking toggle would set expectedOnline to false (i.e., require a reason)
  const computeCurrentExpected = (): boolean =>
    typeof initialIsExpectedOnline === "boolean"
      ? initialIsExpectedOnline
      : true;
  const nextWouldBeFalse = computeCurrentExpected() === true;
  const hasExpectedReasonSelected = (): boolean => {
    const reason = selectedReason;

    if (reason === "Other") {
      return customReason.trim().length > 0;
    }

    return typeof reason === "string" && reason.trim().length > 0;
  };
  const hasErrorReasonSelected = (): boolean => {
    const reason = errorReason;

    if (reason === "Other") {
      return customErrorReason.trim().length > 0;
    }

    return typeof reason === "string" && reason.trim().length > 0;
  };
  const isAnyReasonValidForToggleToFalse = (): boolean => {
    if (!nextWouldBeFalse) return true;

    // Only Expected Offline reason should enable the toggle
    return hasExpectedReasonSelected();
  };
  const mustSelectReason =
    nextWouldBeFalse && !isAnyReasonValidForToggleToFalse();
  const disableExpectedReason =
    allowErrorReason &&
    hasErrorReasonSelected() &&
    !hasExpectedReasonSelected();
  const disableErrorReason =
    allowErrorReason &&
    hasExpectedReasonSelected() &&
    !hasErrorReasonSelected();

  const handleReasonChange = (event: SelectChangeEvent<string>): void => {
    const value = event.target.value as string;

    if (value === "") {
      setSelectedReason("");

      setCustomReason("");

      return;
    }

    setSelectedReason(value);
  };

  const handleErrorReasonChange = (event: SelectChangeEvent<string>): void => {
    const value = event.target.value as string;

    if (value === "") {
      setErrorReason("");

      setCustomErrorReason("");

      return;
    }

    setErrorReason(value);
  };

  const clearExpectedReason = (): void => {
    setSelectedReason("");

    setCustomReason("");
  };

  const clearErrorReason = (): void => {
    setErrorReason("");

    setCustomErrorReason("");
  };

  const handleToggleExpectedOnline = async () => {
    // Compute next value by flipping current
    const current =
      typeof initialIsExpectedOnline === "boolean"
        ? initialIsExpectedOnline
        : true;
    const next = !current;

    // Require a reason when toggling to expected offline
    if (next === false && !isAnyReasonValidForToggleToFalse()) {
      return;
    }

    // Only send Expected Offline reason
    const expectedReasonToSend =
      selectedReason === "Other" ? customReason : selectedReason;

    await setExpectedOnline({
      entityId,
      locationId,
      isExpectedOnline: next,
      reason: expectedReasonToSend,
    });

    onClose();
  };

  // Track previous open state to only initialize when dialog opens
  const prevOpenRef = useRef(false);

  useEffect(() => {
    // Only run initialization when dialog transitions from closed to open
    if (!open || prevOpenRef.current === open) {
      prevOpenRef.current = open;

      return;
    }

    prevOpenRef.current = open;

    setSelectedReason("");

    const normalizedWatcher =
      typeof existingAssignedWatcher === "string"
        ? existingAssignedWatcher.toUpperCase()
        : undefined;

    if (
      normalizedWatcher === "CLEANCONNECT" ||
      normalizedWatcher === "THIRD_PARTY" ||
      normalizedWatcher === "CUSTOMER"
    ) {
      setAssignedWatcher(
        normalizedWatcher as "CLEANCONNECT" | "THIRD_PARTY" | "CUSTOMER"
      );
    } else {
      setAssignedWatcher("CLEANCONNECT");
    }

    // Initialize expected offline reason and error reason from existingReason if present
    const reasonsFromExisting =
      typeof existingReason === "string" && existingReason.trim().length > 0
        ? existingReason
            .split("|")
            .map(r => r.trim())
            .filter(Boolean)
        : [];
    const expectedOfflineReason = reasonsFromExisting[0];
    const existingErrorReason = reasonsFromExisting[1];

    const validReasons = REASONS_BY_ENTITY_TYPE[entityType];
    const isRed = !!allowErrorReason;
    const isGrey = initialIsExpectedOnline === false;

    if (isRed) {
      // Default the error reason dropdown from stored reason:
      // prefer the second part (error) if present; else use the first part
      const preferred = existingErrorReason || expectedOfflineReason;

      if (preferred) {
        if (validReasons.includes(preferred)) {
          setErrorReason(preferred);

          setCustomErrorReason("");
        } else {
          setErrorReason("Other");

          setCustomErrorReason(preferred);
        }
      } else {
        setErrorReason("");

        setCustomErrorReason("");
      }
      // Clear expected offline reason to avoid mutual exclusivity blocking

      setSelectedReason("");

      setCustomReason("");
    } else if (isGrey) {
      // Default the expected offline reason dropdown from stored reason:
      // prefer the first part (expected) if present; else use the second part
      const preferred = expectedOfflineReason || existingErrorReason;

      if (preferred) {
        if (validReasons.includes(preferred)) {
          setSelectedReason(preferred);

          setCustomReason("");
        } else {
          setSelectedReason("Other");

          setCustomReason(preferred);
        }
      } else {
        setSelectedReason("");

        setCustomReason("");
      }
      // Clear error reason

      setErrorReason("");

      setCustomErrorReason("");
    } else {
      // Green/online: clear both
      setSelectedReason("");

      setCustomReason("");

      setErrorReason("");

      setCustomErrorReason("");
    }
  }, [
    entityType,
    open,
    initialIsExpectedOnline,
    existingAssignedWatcher,
    existingReason,
    allowErrorReason,
  ]);

  const handleDeleteLeaf = async (): Promise<void> => {
    console.log("entityType", entityType);

    console.log("leafPk", leafPk);

    console.log("leafSk", leafSk);

    if (entityType === "location") {
      onClose();

      return;
    }

    if (leafPk && leafSk) {
      await deleteHealthLeaf({
        healthLeafPartitionKey: leafPk,
        healthLeafSortKey: leafSk,
      });
    }

    onClose();
  };

  const handleClearNote = async (): Promise<void> => {
    if (!leafPk || !leafSk) {
      onClose();

      return;
    }

    await updateHealthLeafMetadata({
      pk1: leafPk,
      sk: leafSk,
      reason: "",
    });

    onClose();
  };

  const handleSaveMetadata = async (): Promise<void> => {
    if (!leafPk || !leafSk) {
      onClose();

      return;
    }

    const reasonToSend =
      selectedReason === "Other" ? customReason : selectedReason;
    const errorReasonToSend =
      errorReason === "Other" ? customErrorReason : errorReason;
    const combinedReason = [reasonToSend, errorReasonToSend]
      .filter(r => typeof r === "string" && r.trim().length > 0)
      .join(" | ");

    await updateHealthLeafMetadata({
      pk1: leafPk,
      sk: leafSk,
      ...(combinedReason ? { reason: combinedReason } : {}),
      ...(assignedWatcher ? { assignedWatcher } : {}),
    });

    onClose();
  };

  const handleConfirmDelete = async (): Promise<void> => {
    setDeleteConfirmOpen(false);

    await handleDeleteLeaf();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          {`${ENTITY_TYPE_LABEL[entityType]}: ${targetLabel}`}
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Tooltip
              title="Please select a expected offline reason"
              disableHoverListener={!mustSelectReason}
              disableFocusListener={!mustSelectReason}
              disableTouchListener={!mustSelectReason}
            >
              <span>
                <Button
                  onClick={handleToggleExpectedOnline}
                  variant="contained"
                  disabled={updating || mustSelectReason}
                >
                  {initialIsExpectedOnline === false
                    ? "Toggle expected online"
                    : "Toggle expected offline"}
                </Button>
              </span>
            </Tooltip>
            {entityType !== "location" && (
              <Button
                color="error"
                onClick={(): void => setDeleteConfirmOpen(true)}
                disabled={deleting}
                variant="outlined"
              >
                Delete leaf item
              </Button>
            )}
          </Stack>

          {entityType !== "location" && (
            <DialogContentText sx={{ mb: 1 }}>
              {shouldShowReason
                ? "To update assigned manager or reason use forms below"
                : "To update assigned manager use form below"}
            </DialogContentText>
          )}
          {!hideReasonForLocationFlipToOn && (
            <FormControl
              fullWidth
              size="medium"
              sx={{ mb: 2, position: "relative" }}
            >
              <InputLabel id="reason-label">Expected Offline Reason</InputLabel>
              <Tooltip
                title="Clear error reason to select an expected offline reason"
                disableHoverListener={!disableExpectedReason}
                disableFocusListener={!disableExpectedReason}
                disableTouchListener={!disableExpectedReason}
              >
                <Box sx={{ display: "block", width: "100%" }}>
                  <Select
                    labelId="reason-label"
                    id="reason-select"
                    value={selectedReason}
                    label="Expected Offline Reason"
                    disabled={disableExpectedReason}
                    onChange={handleReasonChange}
                    sx={{
                      width: "100%",
                      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0,0,0,0.26) !important",
                      },
                      "&.Mui-disabled.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "rgba(0,0,0,0.26) !important",
                        },
                    }}
                  >
                    {REASONS_BY_ENTITY_TYPE[entityType].map(
                      (reason): JSX.Element => (
                        <MenuItem key={reason} value={reason}>
                          {reason}
                        </MenuItem>
                      )
                    )}
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </Box>
              </Tooltip>
              {!!selectedReason && (
                <Tooltip title="Clear expected offline reason">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="clear-expected-reason"
                      onClick={(e: MouseEvent): void => {
                        e.stopPropagation();

                        e.preventDefault();

                        clearExpectedReason();
                      }}
                      sx={{
                        position: "absolute",
                        right: 25,
                        top: 15,
                        zIndex: 2,
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {selectedReason === "Other" && (
                <TextField
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  label="Custom reason"
                  placeholder={`Enter a reason for this ${entityType}`}
                  value={customReason}
                  disabled={disableExpectedReason}
                  onChange={(e): void => setCustomReason(e.target.value)}
                />
              )}
            </FormControl>
          )}
          {allowErrorReason && entityType !== "location" && (
            <FormControl
              fullWidth
              size="medium"
              sx={{ mb: 2, position: "relative" }}
            >
              <InputLabel id="error-reason-label">Error reason</InputLabel>
              <Tooltip
                title="Clear expected offline reason to select an error reason"
                disableHoverListener={!disableErrorReason}
                disableFocusListener={!disableErrorReason}
                disableTouchListener={!disableErrorReason}
              >
                <Box sx={{ display: "block", width: "100%" }}>
                  <Select
                    labelId="error-reason-label"
                    id="error-reason-select"
                    value={errorReason}
                    label="Error reason"
                    disabled={disableErrorReason}
                    onChange={handleErrorReasonChange}
                    sx={{
                      width: "100%",
                      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0,0,0,0.26) !important",
                      },
                      "&.Mui-disabled.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "rgba(0,0,0,0.26) !important",
                        },
                    }}
                  >
                    {REASONS_BY_ENTITY_TYPE[entityType].map(
                      (reason): JSX.Element => (
                        <MenuItem key={`err-${reason}`} value={reason}>
                          {reason}
                        </MenuItem>
                      )
                    )}
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </Box>
              </Tooltip>
              {!!errorReason && (
                <Tooltip title="Clear error reason">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="clear-error-reason"
                      onClick={(e: MouseEvent): void => {
                        e.stopPropagation();

                        e.preventDefault();

                        clearErrorReason();
                      }}
                      sx={{
                        position: "absolute",
                        right: 25,
                        top: 15,
                        zIndex: 2,
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {errorReason === "Other" && (
                <TextField
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  label="Custom error reason"
                  placeholder={`Enter an error reason for this ${entityType}`}
                  value={customErrorReason}
                  disabled={disableErrorReason}
                  onChange={(e): void => setCustomErrorReason(e.target.value)}
                />
              )}
            </FormControl>
          )}
          {entityType !== "location" &&
            typeof existingReason === "string" &&
            existingReason.trim().length > 0 && (
              <Button
                onClick={handleClearNote}
                sx={{ mb: 2 }}
                disabled={updating}
              >
                Clear reason
              </Button>
            )}

          {entityType !== "location" && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                Assigned manager:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={assignedWatcher}
                  onChange={(e): void => {
                    const value = e.target.value as
                      | "CUSTOMER"
                      | "THIRD_PARTY"
                      | "CLEANCONNECT";

                    setAssignedWatcher(value);
                  }}
                  displayEmpty
                  inputProps={{ "aria-label": "assigned-watcher" }}
                >
                  <MenuItem value="CUSTOMER">CUSTOMER</MenuItem>
                  <MenuItem value="THIRD_PARTY">THIRD_PARTY</MenuItem>
                  <MenuItem value="CLEANCONNECT">CLEANCONNECT</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveMetadata}
              disabled={updating || !leafPk || !leafSk}
            >
              Save
            </Button>
            <Button onClick={onClose}>Close</Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={(): void => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete entity health leaf</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Are you sure you want to delete the health leaf for ${entityType} "${targetLabel}"? This cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={(): void => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ToggleTrackingDialog;
